import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import jwtDecode from 'jwt-decode';
import { getJwt, removeJwt, setJwt } from '../services/jwt';
import api from '../services/api';
import UserRoleEnum from '../enums/UserRoleEnum';

interface AuthContextType {
  user: any;
  login: (username: string, password: string) => void;
  logout: () => void;
  isAdmin: boolean;
}

export type OrganizationType = {
  id: number;
  name: string;
};

export type UserType = {
  name: string;
  email: string;
  roles: string[];
  organization: OrganizationType;
};

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const retrieveUserFromToken = (token: string) => {
    return jwtDecode<UserType>(token);
  };

  const retrieveUserFromStorage = (): UserType | null => {
    const jwt = getJwt();

    return jwt ? retrieveUserFromToken(jwt) : null;
  };

  const [user, setUser] = useState<UserType | null>(retrieveUserFromStorage());
  const [isAdmin, setIsAdmin] = useState(false);

  const login = async (username: string, password: string) => {
    try {
      const response = await api.post('/login', {
        username,
        password,
      });
      const { token } = response.data;
      setJwt(token);
      setUser(retrieveUserFromToken(token));
    } catch (e: any) {
      throw new Error(
        e?.response?.status === 401
          ? 'E-mail ou senha incorretos'
          : 'Tente novamente mais tarde ou entre em contato'
      );
    }
  };

  const logout = () => {
    setUser(null);
    removeJwt();
  };

  useEffect(() => {
    setIsAdmin(
      user?.roles?.some(
        (role: string) =>
          role === UserRoleEnum.ROLE_SUPER_ADMIN ||
          role === UserRoleEnum.ROLE_ADMIN
      ) || false
    );
  }, [user]);

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line import/prefer-default-export
export const useAuth = () => useContext(AuthContext);
